// The gene list group, confirmed in "Genes tested" (TGCLOUD-1868, plan B2, D3; decisions 4, 13 and
// 14; hazard 1868-H4).
//
// TAKEN AS PRINTED (the gate ok: the genes printed as a list, the count agreeing): the report's
// genes are on the Test already, and the application proposed the list state "These are every gene
// the laboratory analysed". One action confirms it: "Confirm the gene list (22 genes, as stated)".
//
// WITHHELD (any other gate): the report's genes are NOT on the Test. They are shown as text, each
// pointing to where the report prints it, with no way to add them from here: the clinician enters
// the genes the report lists as analysed with "Add genes" (paste works), and says what the list is,
// with no default.
//
// WHAT THE LIST IS (genesTestedList.status): every gene the laboratory analysed, only some of them,
// or no list captured. "Complete" is not offered for an exome or a genome (decision 13), and is
// unavailable, with the reason, while fewer genes are listed than the report states (decision 14)
// or none are. "Not tested" is only ever read from a complete list.
import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CropFreeIcon from '@mui/icons-material/CropFree';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AddGenesDialog from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/test-record/AddGenesDialog';
import {
  geneKeyOf,
  rowKeyOf
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/test-record/testRecordModel';
import AnchorMark from './AnchorMark';
import GroupCard from './GroupCard';
import { useReadingChecksContext } from './useReadingChecks';
import { highlightSx, useVerification } from './useVerificationView';
import { fieldTarget, geneTarget } from './verificationTargets';
import { completeNotOffered } from './verificationGroups';
import {
  COMPLETE_NOT_OFFERED_FOR_SCOPE,
  ENTER_GENES_BY_HAND,
  GROUP_KIND_TEXT,
  LIST_STATE_QUESTION,
  SHOW_IN_REPORT,
  STATED_COUNT_LABEL,
  UNKNOWN_PLACE_TEXT,
  WITHHELD_GENES_LEAD,
  confirmGeneListLabel,
  coverageCountsText,
  geneListGateText,
  genesTestedCountText,
  noSourceLocationText,
  showMoreText,
  unverifiableText
} from './labReportVerificationVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The gene list's own target: the count the report states is about it (verificationTargets).
const LIST_TARGET = fieldTarget('genesTestedList');

/**
 * Where a withheld gene stands against the report: 'anchored' with its box, else 'unverifiable'
 * (the report could not be shown) or 'unanchored', with why in words. Never nothing: every gene
 * of a withheld list was read, so its place, or that it has none, is said (plan A2, D3).
 */
function withheldGeneState(view, key) {
  const target = geneTarget(key);
  const state = view?.anchorStateOf?.(target);
  if (state && state.state === 'anchored') return state;
  if (view?.reportStatus === 'failed') return { state: 'unverifiable' };
  const symbol = view?.anchorStateOf?.(target, { fields: ['gene'] });
  return {
    state: 'unanchored',
    reasonText:
      (symbol && symbol.state === 'unanchored' && symbol.reasonText) ||
      (state && state.state === 'unanchored' && state.reasonText) ||
      UNKNOWN_PLACE_TEXT
  };
}

// A long withheld list shows this many genes until asked for the rest.
const SHOWN = 12;

const coverageOf = (rows) => {
  const count = (status) =>
    rows.filter((r) => (r?.coverage?.status || 'not-stated') === status).length;
  return {
    complete: count('complete'),
    incomplete: count('incomplete'),
    notStated: count('not-stated')
  };
};

/**
 * @param {object} props
 * @param {object} props.form          the Test form's values now
 * @param {(status: string) => void} props.onSetListState
 * @param {(rows: object[]) => void} props.onAddGenes   Add genes' rows, entered by hand
 */
export default function GeneListCard({ form, onSetListState, onAddGenes }) {
  const t = useAutoTranslation();

  const checks = useReadingChecksContext();
  const view = useVerification();
  const [adding, setAdding] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);
  if (!checks || !checks.active || !checks.geneListGroup) return null;

  const group = checks.geneListGroup;
  const taken = group.confident === true;
  const rows = Array.isArray(form?.genesTested) ? form.genesTested : [];
  const geneList = checks.geneList || {};
  const withheld = taken
    ? []
    : (Array.isArray(geneList.withheld) ? geneList.withheld : []).filter(
        (g) => g && typeof g.symbol === 'string' && g.symbol
      );
  const shown = showAll ? withheld : withheld.slice(0, SHOWN);
  const status = form?.genesTestedList?.status || '';
  const options = checks.listOptions;
  const stateId = `gene-list-state-${group.key}`;
  // A place in the report selected is about the list (the count it states) or one of its genes:
  // each says which, so the Test pane scrolls to it and rings it (VerificationLayout).
  const lit = (target) => !!view?.isHighlighted?.(target);
  const ringed = (target) => highlightSx(lit(target));
  const listMark = (
    <AnchorMark
      target={LIST_TARGET}
      fields={['stated']}
      label={t(STATED_COUNT_LABEL)}
    />
  );

  return (
    <Box data-testid="gene-list-card" data-taken={taken ? 'true' : 'false'}>
      <GroupCard
        groupKey={group.key}
        title={t(GROUP_KIND_TEXT['gene-list'])}
        confirmLabel={confirmGeneListLabel(
          {
            statedCount: taken ? checks.statedCount : null,
            rowCount: rows.length
          },
          t
        )}
      >
        <Typography
          variant="body2"
          component="div"
          data-testid="genes-tested-count"
          {...(taken
            ? {
                'data-verification-target': LIST_TARGET,
                'data-highlighted': lit(LIST_TARGET) ? 'true' : undefined
              }
            : {})}
          sx={taken ? ringed(LIST_TARGET) : undefined}
        >
          {genesTestedCountText(rows.length, t)}
          {taken && listMark}
        </Typography>
        {rows.length > 0 && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary' }}
          >
            {coverageCountsText(coverageOf(rows), t)}
          </Typography>
        )}

        {!taken && (
          <Box data-testid="gene-list-withheld" sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              component="div"
              data-testid="gene-list-gate"
              data-verification-target={LIST_TARGET}
              data-highlighted={lit(LIST_TARGET) ? 'true' : undefined}
              sx={ringed(LIST_TARGET)}
            >
              {geneListGateText(geneList, t)}
              {listMark}
            </Typography>
            {withheld.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'text.secondary', mt: 0.5 }}
                >
                  {t(WITHHELD_GENES_LEAD)}
                </Typography>
                <Box
                  data-testid="withheld-genes"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 0.5
                  }}
                >
                  {shown.map((g, i) => {
                    const key = geneKeyOf(g.symbol);
                    const target = geneTarget(key);
                    const where = withheldGeneState(view, key);
                    const located = where.state === 'anchored';
                    // Said, never left blank: why a gene has no place to show (AnchorMark's words;
                    // reasonText is the view's own, never data).
                    const placeless =
                      where.state === 'unverifiable'
                        ? unverifiableText(t)
                        : noSourceLocationText(t(where.reasonText), t);
                    return (
                      <Box
                        key={`${g.symbol}-${i}`}
                        component="span"
                        data-verification-target={target}
                        data-highlighted={lit(target) ? 'true' : undefined}
                        sx={{
                          display: 'inline-flex',
                          borderRadius: 4,
                          ...ringed(target)
                        }}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={g.symbol}
                          data-testid="withheld-gene"
                          data-anchor-state={where.state}
                          icon={
                            located ? (
                              <CropFreeIcon />
                            ) : where.state === 'unverifiable' ? (
                              <VisibilityOffOutlinedIcon />
                            ) : (
                              <LocationOffOutlinedIcon />
                            )
                          }
                          title={located ? undefined : placeless}
                          aria-label={
                            located
                              ? `${t(SHOW_IN_REPORT)}: ${g.symbol}`
                              : `${g.symbol}. ${placeless}`
                          }
                          onClick={
                            located
                              ? () => view.showInReport(target)
                              : undefined
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
                {withheld.length > SHOWN && (
                  <Button size="small" onClick={() => setShowAll((v) => !v)}>
                    {showMoreText(showAll, withheld.length, t)}
                  </Button>
                )}
              </>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() => setAdding(true)}
            >
              {t(ENTER_GENES_BY_HAND)}
            </Button>
          </Box>
        )}

        <FormControl
          component="fieldset"
          sx={{ mt: 1, display: 'block' }}
          data-testid="gene-list-state"
        >
          <FormLabel
            component="legend"
            id={stateId}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            {t(LIST_STATE_QUESTION)}
          </FormLabel>
          <RadioGroup
            aria-labelledby={stateId}
            value={options.some((o) => o.value === status) ? status : ''}
            onChange={(e) => onSetListState(e.target.value)}
          >
            {options.map((o) => (
              <Box key={o.value}>
                <FormControlLabel
                  value={o.value}
                  disabled={o.disabled}
                  control={<Radio size="small" />}
                  label={t(o.label)}
                />
                {/* Said in this language already (useReadingChecks.listOptions). */}
                {o.reason && (
                  <FormHelperText sx={{ mt: -0.5, ml: 4 }}>
                    {o.reason}
                  </FormHelperText>
                )}
              </Box>
            ))}
          </RadioGroup>
          {completeNotOffered(form?.testScope) && (
            <FormHelperText data-testid="complete-not-offered">
              {t(COMPLETE_NOT_OFFERED_FOR_SCOPE)}
            </FormHelperText>
          )}
        </FormControl>
      </GroupCard>
      <AddGenesDialog
        open={adding}
        listedKeys={new Set(rows.map(rowKeyOf))}
        onClose={() => setAdding(false)}
        onAdd={(added) => {
          onAddGenes(added);
          setAdding(false);
        }}
      />
    </Box>
  );
}
